/**
 * ErrorScreen - 통합 에러 화면 컴포넌트
 * variant에 따라 인증 에러 또는 일반 에러 화면 표시
 */

import React from 'react';
import styles from './ErrorScreen.module.css';
import { InfoCircleIcon } from '../Icons/Icons';

export type ErrorScreenVariant = 'auth' | 'error';

export interface ErrorScreenProps {
  /** 에러 화면 종류 */
  variant: ErrorScreenVariant;
  /** 에러 제목 */
  title: string;
  /** 에러 메시지 */
  message: string | React.ReactNode;
  /** 뒤로가기 콜백 (제공 시 뒤로가기 버튼 표시) */
  onBack?: () => void;
  /** 다시 시도 콜백 (제공 시 다시 시도 버튼 표시) */
  onRetry?: () => void;
}

export const ErrorScreen: React.FC<ErrorScreenProps> = ({
  variant,
  title,
  message,
  onBack,
  onRetry,
}) => {
  const containerClass =
    variant === 'auth'
      ? `${styles.container} ${styles.containerAuth}`
      : `${styles.container} ${styles.containerError}`;

  // variant에 따라 다른 aria-live 값 (auth는 덜 긴급, error는 긴급)
  const ariaLive = variant === 'auth' ? 'polite' : 'assertive';

  return (
    <div className={containerClass} role="alert" aria-live={ariaLive}>
      <InfoCircleIcon size={64} className={styles.icon} aria-hidden />
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.message}>{message}</div>
      {onBack && (
        <button className={styles.backButton} onClick={onBack}>
          돌아가기
        </button>
      )}
      {onRetry && (
        <button className={styles.backButton} onClick={onRetry}>
          다시 시도
        </button>
      )}
    </div>
  );
};

// 편의를 위한 사전 정의된 컴포넌트
export const AuthRequiredError: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorScreen
    variant="auth"
    title="인증이 필요합니다"
    message={
      <>
        이 서비스는 인증이 필요합니다.
        <br />
        로그인 후 다시 시도해 주세요.
      </>
    }
    onRetry={onRetry}
  />
);

export const DefaultError: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => (
  <ErrorScreen variant="error" title="문제가 발생했습니다" message={message} onRetry={onRetry} />
);

export const ForbiddenError: React.FC<{
  message?: string;
  onBack?: () => void;
}> = ({ message = '접근 권한이 없습니다.', onBack }) => (
  <ErrorScreen
    variant="error"
    title="접근 권한이 없는 페이지입니다."
    message={message}
    onBack={onBack}
  />
);

export const NetworkErrorScreen: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorScreen
    variant="error"
    title="네트워크 오류"
    message={
      <>
        서버에 연결할 수 없습니다.
        <br />
        네트워크 연결을 확인한 후 다시 시도해주세요.
      </>
    }
    onRetry={onRetry}
  />
);
